import { useContext, useEffect } from "react"
import { type Token, type ExAppId } from "./types"
import { AppStateContext } from "./appState"
import { Success, Loading, Failure } from "@heyhabito/remote-data"
import * as t from "io-ts"
import { getEligibilities } from "./api"
import { useSentry } from "@heyhabito/sentry"

export interface Eligibilities {
  eligibleForPlus: boolean | null
  eligibleForSelfServePlus: boolean
  hasFoundSolicitor: boolean | null
}

export const eligibilitySchema = t.strict({
  eligibleForPlus: t.union([t.null, t.boolean]),
  eligibleForSelfServePlus: t.union([t.null, t.boolean]),
  hasFoundSolicitor: t.union([t.null, t.boolean]),
})

export const useUpdateEligibilities = (
  token: Token | null,
  exAppId: ExAppId | null
): void => {
  const { logToSentryDetailed } = useSentry()
  const {
    state: { eligibilities },
    dispatch,
  } = useContext(AppStateContext)

  useEffect(() => {
    if (!token || !exAppId) {
      return
    }

    switch (eligibilities._tag) {
      case "NotAsked":
        dispatch({
          type: "setEligibilities",
          eligibilities: Loading,
        })

        getEligibilities(token)(exAppId)
          .then(response => {
            dispatch({
              type: "setEligibilities",
              eligibilities: Success(response),
            })
          })
          .catch(error => {
            const errorName = error == null ? null : error.name
            const errorMessage = error == null ? null : error.message
            dispatch({
              type: "setEligibilities",
              eligibilities: Failure(error),
            })
            logToSentryDetailed({
              description: "unable to retrieve eligibilities",
              info: {
                cause: error,
              },
              tags: {
                exAppId: exAppId,
                errorName,
                errorMessage,
              },
            })
          })

        break
      default:
        return
    }
  }, [exAppId, eligibilities, token, logToSentryDetailed, dispatch])
}
